/**
 * Complete Backtesting — the day-ahead book and the intraday book running at the same time.
 *
 * Three legs, one position limit:
 *   • day ahead — the Day Ahead Backtesting engine unchanged (Goose + the carry policy), with the
 *                 Goose results of the Goose settings chosen here, cached per day and computed on
 *                 Run when missing (components/GooseBookPanel.js);
 *   • intraday  — new positions from the intraday substrate, openable from 4 h before the
 *                 product's gate opens until 10 min before gate closure, held to balancing;
 *   • reversal  — close a day-ahead position on the intraday market when the intraday SK
 *                 forecast has turned against it by more than the threshold. OFF by default.
 *
 * The position limit is COMBINED: per product (PH = the hour, QH = the quarter) the day-ahead
 * volume carried into delivery plus the intraday fills may not exceed it. Fees are charged the same
 * way at every venue (transaction % of sales + PLN/MWh), intraday and balancing included.
 *
 * Layout and statistics follow the Day Ahead tab (components/BacktestKit.js); the day-ahead leg's
 * cards are that tab's, fed with the same report.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CalendarClock, Layers, Play, TrendingUp } from "lucide-react";
import { useApi } from "../hooks/useApi";
import GooseBookPanel, { detailText, useGooseBook } from "../components/GooseBookPanel";
import {
  BreakdownCard, Bars, CumChart, DA_DEFAULT_PARAMS, DailyList, FeeControls, KpiCard, MODEL_LABEL, PolicyControls, SectionCard,
  SkAccuracyCard, SplitSummary, TIME_LABEL, Th, VolumeAndDeferrals, daQueryPairs, fmtInt, fmtNum, fmtSigned, signColor, defaultDates, tones,
} from "../components/BacktestKit";

export const DEFAULT_PARAMS = {
  ...DA_DEFAULT_PARAMS,
  // combined position limit, MW per product
  pos_limit_mw: 50,
  // intraday leg
  intraday_on: true,
  sell_floor: 100, buy_floor: 600, buy_floor_mid: 650, buy_floor_long: 800,
  buy_margin: -10, sell_margin: -10,
  anchor_tol: 8,
  // reversal close — off by default
  rev_close_on: false,
  rev_thr: 300,
};

const CompleteBacktesting = ({ darkMode }) => {
  const dark = !!darkMode;
  const t = tones(dark);
  const { fetchGet, fetchPost, isAuthenticated } = useApi();
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const goose = useGooseBook(meta?.goose, fetchPost, start, end);
  const [result, setResult] = useState(null);
  const [runQuery, setRunQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v }));
  const num = (e) => (e.target.value === "" ? "" : Number(e.target.value));

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetchGet("/complete/meta");
        const data = await res.json();
        if (!res.ok || data.status !== "success") throw new Error(data.detail || "Failed to load the data bundle.");
        if (!alive) return;
        setMeta(data);
        const dates = defaultDates(data.first_day);
        setStart(dates.start);
        setEnd(dates.end);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load the data bundle.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, fetchGet]);

  const queryString = useCallback(
    (extra = {}) => {
      const qp = new URLSearchParams(daQueryPairs(params));
      if (start) qp.set("start", start);
      if (end) qp.set("end", end);
      Object.entries(extra).forEach(([k, v]) => qp.set(k, String(v)));
      return qp.toString();
    },
    [params, start, end]
  );

  const run = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    setError(null);
    try {
      const key = await goose.ensure();
      const qs = queryString({ goose: key });
      const res = await fetchGet(`/complete/run?${qs}`);
      const data = await res.json();
      if (!res.ok || data.status !== "success") throw new Error(detailText(data.detail, "run failed"));
      setResult(data);
      setRunQuery(qs);
    } catch (e) {
      setError(e.message || "run failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [meta, goose, fetchGet, queryString]);

  const fetchDay = useCallback(
    async (day) => {
      const res = await fetchGet(`/dayahead/day?${runQuery}&day=${day}`);
      const data = await res.json();
      return res.ok && data.status === "success" ? data : { hours: [] };
    },
    [fetchGet, runQuery]
  );

  const c = result?.combined;
  const da = result?.day_ahead;
  const ds = da?.summary;
  const id = result?.intraday;
  const rev = id?.reversal;
  const months = useMemo(
    () => (result?.by_month || []).map((m) => ({ ...m, added_pln: Number(m.total_pnl) - Number(m.goose_pnl) })),
    [result]
  );
  const days = useMemo(() => result?.by_day || [], [result]);
  const i2Label = TIME_LABEL[result?.params?.i2_time || params.i2_time] || params.i2_time;
  const sameGoose = da?.goose_book && goose.status?.key === da.goose_book.key;
  const intradayCloses = id ? [{ venue: "Balancing market · intraday positions (outside the day-ahead book)", goose_mwh: 0, policy_mwh: id.mwh }] : [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>Complete Backtesting</h1>
        <p className={`text-xs mt-1 ${t.faint}`}>
          The day-ahead book (IDA1-Goose + the carry policy) and the intraday book at the same time, under one combined position limit.
          Intraday positions may be opened from 4 h before a product&apos;s gate opens until 10 min before gate closure.
          {meta ? ` Data ${meta.first_day} → ${meta.last_day}; the intraday substrate starts ${meta.intraday_first_day}, so earlier days are day-ahead only.` : ""}
        </p>
      </div>

      {/* ---------------- Configuration ---------------- */}
      <SectionCard title="Configuration" icon={<Activity className="w-4 h-4" />} dark={dark}>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label className={t.labelCls}>Start Date</label>
            <input type="date" className={t.inputCls} value={start} min={meta?.first_day} max={meta?.last_day} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className={t.labelCls}>End Date</label>
            <input type="date" className={t.inputCls} value={end} min={meta?.first_day} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div>
            <label className={t.labelCls}>SK forecast model</label>
            <div className={`${t.inputCls} opacity-80`} title="Walk-forward v50-k (Kumo-Tabular) SK forecast at every decision time.">
              {MODEL_LABEL[params.model] || params.model}
            </div>
          </div>
          <div>
            <label className={t.labelCls} title="Per product (PH = the hour, QH = the quarter): day-ahead volume carried into delivery PLUS intraday fills. Empty = no limit.">
              Combined position limit (MW)
            </label>
            <input type="number" step="5" min="0" className={t.inputCls} value={params.pos_limit_mw ?? ""} placeholder="no limit" onChange={(e) => setParam("pos_limit_mw", num(e))} />
          </div>
          <div>
            <label className={t.labelCls} title="MW per quarter and side, hourly and 15-minute products together: an hourly position (Fix I, BASE) counts its MW in each of its four quarters, a 15-minute one (Fix II bucket) only in its own; 1 MW for a quarter = 0.25 MWh. The whole position open after Fixing II: Goose's own IDA1-bound volume plus the extra kept open. Goose volume above the cap is closed at Fixing II instead of IDA1. Empty = no cap.">
              Max open after Fixing II (MW / quarter)
            </label>
            <input type="number" step="5" min="0" className={t.inputCls} value={params.cap_fix2_mw ?? ""} placeholder="no cap" onChange={(e) => setParam("cap_fix2_mw", num(e))} />
          </div>
          <div>
            <label className={t.labelCls} title="MW per quarter and side, hourly and 15-minute products together: an hourly position (Fix I, BASE) counts its MW in each of its four quarters, a 15-minute one (Fix II bucket) only in its own; 1 MW for a quarter = 0.25 MWh. The most that may stay open past IDA1 (towards IDA2 / IDA3 / balancing). Empty = no cap.">
              Max carried past IDA1 (MW / quarter)
            </label>
            <input type="number" step="5" min="0" className={t.inputCls} value={params.cap_ida1_mw ?? ""} placeholder="no cap" onChange={(e) => setParam("cap_ida1_mw", num(e))} />
          </div>
        </div>

        <div className="mb-4">
          <GooseBookPanel gooseMeta={meta?.goose} goose={goose} gooseTotal={sameGoose ? da.goose_book.goose_total_pln : null}
                          dark={dark} inputCls={t.inputCls} labelCls={t.labelCls} />
        </div>

        <PolicyControls params={params} setParam={setParam} ida2Times={meta?.ida2_times} dark={dark} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
          <div className={t.stageCls}>
            <div className={t.stageTitle}>Intraday leg — new positions, held to balancing</div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!params.intraday_on} onChange={(e) => setParam("intraday_on", e.target.checked)} />
              <span className={t.muted}>open new intraday positions (4 h before gate open … 10 min before gate closure)</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                ["sell_floor", "SELL floor (MW)", 50, "SELL when the intraday SK forecast is at most −floor."],
                ["buy_floor", "BUY floor (MW)", 50, "Lead-dependent BUY ramp: ≤60 min uses this floor, ≤120 min the mid floor, beyond the long one (650 / 800)."],
                ["buy_margin", "Buy margin (€/MWh)", 5, "Required edge for a BUY: price below the CEB forecast by at least the negative of this margin."],
                ["sell_margin", "Sell margin (€/MWh)", 5, "Required edge for a SELL."],
              ].map(([k, label, step, tip]) => (
                <div key={k}>
                  <label className={t.labelCls} title={tip}>{label}</label>
                  <input type="number" step={step} className={t.inputCls} value={params[k]} disabled={!params.intraday_on} onChange={(e) => setParam(k, num(e))} />
                </div>
              ))}
            </div>
          </div>
          <div className={t.stageCls}>
            <div className={t.stageTitle}>Close on SK reversal (off by default)</div>
            <label className="flex items-center gap-2 text-xs"
                   title="Close a day-ahead position on the intraday market when the intraday SK forecast has turned against it by more than the threshold. Held short + forecast ≥ +thr → buy it back; held long + forecast ≤ −thr → sell it out.">
              <input type="checkbox" checked={!!params.rev_close_on} onChange={(e) => setParam("rev_close_on", e.target.checked)} />
              <span className={t.muted}>enabled</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={t.labelCls}>SK reversal threshold (MW)</label>
                <input type="number" step="50" min="0" className={t.inputCls} value={params.rev_thr} disabled={!params.rev_close_on} onChange={(e) => setParam("rev_thr", num(e))} />
              </div>
            </div>
            <div className={`text-[11px] ${t.faint}`}>Closes execute on an opposite-direction intraday candidate — the only place an order-book ladder exists.</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FeeControls params={params} setParam={setParam} dark={dark} />
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={run}
            disabled={loading || !meta || !goose.settings}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Play className="w-4 h-4" />
            {loading ? (goose.status?.state === "computing" || goose.status?.state === "queued" ? "Computing Goose results…" : "Running…") : "Run Backtest"}
          </button>
          <button onClick={() => { setParams(DEFAULT_PARAMS); goose.reset(); }} className={t.btnSecondary}>
            Reset to defaults
          </button>
        </div>
      </SectionCard>

      {error ? <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 px-4 py-3 text-sm">{error}</div> : null}
      {result?.notes?.length || id?.note ? (
        <div className={`rounded-xl border px-4 py-3 text-xs ${dark ? "bg-amber-950/30 border-amber-900 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          {(result.notes || []).map((n, i) => (
            <div key={i}>{n}</div>
          ))}
          {id?.note ? <div>Intraday: {id.note}.</div> : null}
        </div>
      ) : null}
      {!result && !loading && !error && meta ? <div className={`text-sm ${t.muted}`}>Choose the dates and press Run Backtest.</div> : null}

      {/* ---------------- Summary (all legs) ---------------- */}
      {c ? (
        <SectionCard title="Summary" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
          <div className={`flex flex-wrap gap-x-8 gap-y-2 mb-5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
            <div>
              <span className="font-semibold">{fmtInt(c.days)}</span> days ({c.start} → {c.end}) ·{" "}
              <span className="text-emerald-600 font-semibold">{fmtInt(c.profitable_days)} profitable</span> /{" "}
              <span className="text-rose-600 font-semibold">{fmtInt(c.loss_days)} loss</span>
            </div>
            <div>
              vs day-ahead only: <span className="text-emerald-600 font-semibold">{fmtInt(c.days_better)} better</span> /{" "}
              <span className="text-rose-600 font-semibold">{fmtInt(c.days_worse)} worse</span> days ·{" "}
              <span className="font-semibold">{fmtInt(c.months_better)}</span> of {fmtInt(c.months)} months better
            </div>
            <div title={`Day-ahead leg ${fmtInt(c.day_ahead_fees)} PLN, intraday leg ${fmtInt(c.intraday_fees)} PLN. Every venue pays the transaction and volume fee, the balancing market included.`}>
              Fees: <span className="font-semibold text-rose-600">−{fmtInt(c.total_fees)} PLN</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
            <KpiCard dark={dark} label="Total P&L" value={fmtInt(c.total_pnl)} sub="PLN · all three legs" accent={signColor(c.total_pnl, dark)} />
            <KpiCard dark={dark} label="P&L After Fees" value={fmtInt(c.pnl_after_fees)} sub={`PLN · −${fmtInt(c.total_fees)} fees`} accent={signColor(c.pnl_after_fees, dark)} />
            <KpiCard dark={dark} label="Day-ahead only" value={fmtInt(c.goose_pnl)} sub="PLN · Goose, closed by IDA1" />
            <KpiCard dark={dark} label="Added by the BM carry" value={fmtSigned(c.policy_pln)} sub={`PLN · day ahead + BM ${fmtInt(c.day_ahead_pln)}`} accent={signColor(c.policy_pln, dark)} />
            <KpiCard dark={dark} label="Intraday (new positions)" value={fmtSigned(c.intraday_pln)} sub={`PLN · ${fmtInt(id?.trades)} trades · ${fmtInt(id?.mwh)} MWh`} accent={signColor(c.intraday_pln, dark)} />
            <KpiCard dark={dark} label="Reversal closes" value={rev?.on ? fmtSigned(c.reversal_pln) : "off"} sub={rev?.on ? `PLN · ${fmtInt(rev?.closes)} closes` : "enable above to simulate"} accent={rev?.on ? signColor(c.reversal_pln, dark) : ""} />
            <KpiCard dark={dark} label="PLN / MWh" value={fmtNum(c.pln_per_mwh, 2)} sub={`day-ahead only ${fmtNum(c.goose_pln_per_mwh, 2)}`} accent={signColor(c.pln_per_mwh, dark)} />
            <KpiCard dark={dark} label="Left for balancing" value={fmtInt(Number(c.rb_mwh) + Number(c.intraday_mwh))} sub={`MWh · day-ahead ${fmtInt(c.rb_mwh)} + intraday ${fmtInt(c.intraday_mwh)}`} />
          </div>

          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={t.headCls}>
                  <Th left>Metric</Th>
                  <Th>Day-ahead only (Goose)</Th>
                  <Th>Day ahead + BM</Th>
                  <Th>Complete (+ intraday)</Th>
                  <Th>Difference vs day-ahead only</Th>
                </tr>
              </thead>
              <tbody className={t.bodyCls}>
                {[
                  { l: "P&L (PLN)", g: c.goose_pnl, b: c.day_ahead_pln, c: c.total_pnl, money: true },
                  { l: "Fees (PLN)", g: c.goose_fees, b: c.day_ahead_fees, c: c.total_fees },
                  { l: "P&L after fees (PLN)", g: c.goose_pnl_after_fees, b: ds ? ds.pnl_after_fees : null, c: c.pnl_after_fees, money: true },
                  { l: "PLN / MWh", g: c.goose_pln_per_mwh, b: ds ? ds.pln_per_mwh : null, c: c.pln_per_mwh, dec: 2 },
                  { l: "Traded + settled volume (MWh)", g: c.goose_traded_mwh, b: ds ? ds.traded_mwh : null, c: c.traded_mwh },
                  { l: "Profitable days", g: c.goose_profitable_days, b: ds ? ds.profitable_days : null, c: c.profitable_days },
                ].map((r) => {
                  const diff = r.g == null || r.c == null ? null : Number(r.c) - Number(r.g);
                  const f = (v) => (r.dec != null ? fmtNum(v, r.dec) : fmtInt(v));
                  return (
                    <tr key={r.l} className={`border-t ${t.rowBorder}`}>
                      <td className={`py-2 px-2 text-xs ${t.muted}`}>{r.l}</td>
                      <td className="py-2 px-2 text-right">{f(r.g)}</td>
                      <td className={`py-2 px-2 text-right ${r.money ? signColor(r.b, dark) : ""}`}>{f(r.b)}</td>
                      <td className={`py-2 px-2 text-right font-semibold ${r.money ? signColor(r.c, dark) : ""}`}>{f(r.c)}</td>
                      <td className={`py-2 px-2 text-right ${r.money ? signColor(diff, dark) : ""}`}>
                        {diff == null ? "–" : r.dec != null ? `${diff > 0 ? "+" : ""}${fmtNum(diff, r.dec)}` : fmtSigned(diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Intraday leg ---------------- */}
      {c && id ? (
        <SectionCard title="Intraday leg · new positions held to balancing" icon={<Layers className="w-4 h-4" />} dark={dark}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <KpiCard dark={dark} label="Intraday P&L" value={fmtSigned(id.pnl_pln)} sub={`PLN · ${fmtSigned(id.pnl_eur)} EUR`} accent={signColor(id.pnl_pln, dark)} />
            <KpiCard dark={dark} label="After fees" value={fmtSigned(id.pnl_after_fees_pln)} sub={`PLN · −${fmtInt(id.fees_pln)} fees`} accent={signColor(id.pnl_after_fees_pln, dark)} />
            <KpiCard dark={dark} label="Trades" value={fmtInt(id.trades)} sub={`of ${fmtInt(id.rows_considered)} candidates`} />
            <KpiCard dark={dark} label="Volume" value={fmtInt(id.mwh)} sub="MWh opened" />
            <KpiCard dark={dark} label="EUR / MWh" value={fmtNum(id.eur_per_mwh, 1)} sub="per MWh opened" accent={signColor(id.eur_per_mwh, dark)} />
            <KpiCard dark={dark} label="Blocked by the limit" value={fmtInt(id.capped_out)} sub="decisions with no room left" />
          </div>
          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={t.headCls}>
                  <Th left>Side</Th>
                  <Th>Trades</Th>
                  <Th>MWh</Th>
                  <Th>P&L (PLN)</Th>
                  <Th>PLN / MWh</Th>
                  <Th>Fees (PLN)</Th>
                </tr>
              </thead>
              <tbody className={t.bodyCls}>
                {(id.by_side || []).map((r) => (
                  <tr key={r.side} className={`border-t ${t.rowBorder}`}>
                    <td className={`py-2 px-2 text-xs font-semibold ${r.side === "SELL" ? "text-rose-600" : "text-emerald-600"}`}>{r.side}</td>
                    <td className="py-2 px-2 text-right">{fmtInt(r.trades)}</td>
                    <td className="py-2 px-2 text-right">{fmtInt(r.mwh)}</td>
                    <td className={`py-2 px-2 text-right font-medium ${signColor(r.pnl_pln, dark)}`}>{fmtSigned(r.pnl_pln)}</td>
                    <td className={`py-2 px-2 text-right ${signColor(r.pln_per_mwh, dark)}`}>{fmtNum(r.pln_per_mwh, 1)}</td>
                    <td className="py-2 px-2 text-right">{fmtInt(r.fees_pln)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rev?.on ? (
            <div className={`mt-3 text-xs ${t.muted}`}>
              Reversal closes: {fmtInt(rev.closes)} closes, {fmtInt(rev.mwh)} MWh, {fmtSigned(rev.gain_pln)} PLN vs settling them on the balancing market.
            </div>
          ) : null}
        </SectionCard>
      ) : null}

      {/* ---------------- Day-ahead leg (the Day Ahead tab's cards) ---------------- */}
      {ds ? (
        <SectionCard title="Day-ahead book · exchange vs balancing market" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
          <SplitSummary split={da.split} gooseTotal={ds.goose_pnl} dark={dark} />
        </SectionCard>
      ) : null}
      {ds ? <VolumeAndDeferrals report={da} extraCloses={intradayCloses} dark={dark} /> : null}
      {ds ? <BreakdownCard report={da} dark={dark} /> : null}
      {ds ? <SkAccuracyCard report={da} model={params.model} dark={dark} /> : null}

      {/* ---------------- Charts ---------------- */}
      {c ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Cumulative P&L" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
            <CumChart rows={days} dark={dark} />
            <div className={`mt-2 flex justify-between text-[11px] ${t.faint}`}>
              <span>{days[0]?.day || ""}</span>
              <span>
                <span className={t.muted}>━ day-ahead only {fmtInt(c.goose_pnl)}</span>
                {"   "}
                <span className="text-emerald-600 font-semibold">━ complete {fmtInt(c.total_pnl)} PLN</span>
              </span>
              <span>{days.length ? days[days.length - 1].day : ""}</span>
            </div>
          </SectionCard>
          <SectionCard title="Added vs day-ahead only, by month" icon={<Activity className="w-4 h-4" />} dark={dark}>
            <Bars rows={months} valueKey="added_pln" labelKey="month" dark={dark} fmt={fmtSigned} />
          </SectionCard>
        </div>
      ) : null}

      {/* ---------------- Monthly ---------------- */}
      {c ? (
        <SectionCard title="Monthly breakdown" icon={<CalendarClock className="w-4 h-4" />} dark={dark}>
          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={t.headCls}>
                  <Th left>Month</Th>
                  <Th>Days</Th>
                  <Th>Day-ahead only (PLN)</Th>
                  <Th>BM carry (PLN)</Th>
                  <Th>Day ahead + BM (PLN)</Th>
                  <Th>Intraday (PLN)</Th>
                  <Th>Intraday trades</Th>
                  <Th>Intraday (MWh)</Th>
                  <Th>Reversal (PLN)</Th>
                  <Th>Complete (PLN)</Th>
                  <Th>Balancing, day-ahead (MWh)</Th>
                  <Th>Intraday fees (PLN)</Th>
                </tr>
              </thead>
              <tbody className={t.bodyCls}>
                {months.map((m) => (
                  <tr key={m.month} className={`border-t ${t.rowBorder}`}>
                    <td className="py-1.5 px-2 text-xs font-medium">{m.month}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.days)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.goose_pnl)}</td>
                    <td className={`py-1.5 px-2 text-right ${signColor(m.policy_pln, dark)}`}>{fmtSigned(m.policy_pln)}</td>
                    <td className={`py-1.5 px-2 text-right ${signColor(m.day_ahead_pln, dark)}`}>{fmtInt(m.day_ahead_pln)}</td>
                    <td className={`py-1.5 px-2 text-right ${signColor(m.intraday_pln, dark)}`}>{fmtSigned(m.intraday_pln)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.intraday_trades)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.intraday_mwh)}</td>
                    <td className={`py-1.5 px-2 text-right ${signColor(m.reversal_pln, dark)}`}>{m.reversal_pln ? fmtSigned(m.reversal_pln) : "·"}</td>
                    <td className={`py-1.5 px-2 text-right font-semibold ${signColor(m.total_pnl, dark)}`}>{fmtInt(m.total_pnl)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.rb_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.intraday_fees)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={`mt-2 text-[11px] ${t.faint}`}>
            Day-ahead only = Goose as it runs itself (closed by IDA1); BM carry = what the carry policy adds on top (Day Ahead tab); Intraday = only NEWLY
            opened intraday positions, never a carry-over from the auctions; Reversal = closing a carried day-ahead position early on the intraday market.
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Daily ---------------- */}
      {c ? (
        <SectionCard title="Daily results · day ahead, balancing and intraday" icon={<Activity className="w-4 h-4" />} dark={dark}>
          <DailyList
            rows={days}
            fetchDay={fetchDay}
            i2Label={i2Label}
            dark={dark}
            stats={(d) => (
              <>
                <span>Balancing: <span className="font-medium">{fmtNum(d.rb_mwh, 0)}</span> MWh</span>
                <span>Intraday: <span className="font-medium">{fmtInt(d.intraday_trades)}</span> trades · <span className="font-medium">{fmtNum(d.intraday_mwh, 0)}</span> MWh</span>
                <span>Day-ahead: <span className="font-medium">{fmtInt(d.goose_pnl)}</span></span>
                <span className={`font-medium ${signColor(d.policy_pln, dark)}`} title="BM carry">{fmtSigned(d.policy_pln)}</span>
                <span className={`font-medium ${signColor(d.intraday_pln, dark)}`} title="Intraday">{fmtSigned(d.intraday_pln)}</span>
                {d.reversal_pln ? <span className={`font-medium ${signColor(d.reversal_pln, dark)}`} title="Reversal">{fmtSigned(d.reversal_pln)}</span> : null}
                <span className={`font-bold text-sm ${signColor(d.total_pnl, dark)}`}>{fmtInt(d.total_pnl)} PLN</span>
              </>
            )}
            extra={(d) => (
              <div className={`flex flex-wrap gap-x-6 gap-y-1 mb-2 text-xs ${t.muted}`}>
                <span>Intraday: <span className={`font-medium ${signColor(d.intraday_pln, dark)}`}>{fmtSigned(d.intraday_pln)} PLN</span> on {fmtInt(d.intraday_trades)} trades / {fmtNum(d.intraday_mwh, 1)} MWh (fees {fmtInt(d.intraday_fees)} PLN)</span>
                {d.reversal_closes ? <span>Reversal: {fmtInt(d.reversal_closes)} closes, {fmtSigned(d.reversal_pln)} PLN</span> : null}
                <span>Complete day: <span className={`font-semibold ${signColor(d.total_pnl, dark)}`}>{fmtInt(d.total_pnl)} PLN</span></span>
              </div>
            )}
          />
        </SectionCard>
      ) : null}
    </div>
  );
};

export default CompleteBacktesting;
